import { ErrorMapper } from '../../core/error/errorMapper';
import { AppErrors } from '../../core/error/appError';
import { failure, success, type AppResult } from '../../core/result/appResult';
import { currentlyConnected } from '../repository/apiCall';

/**
 * Cliente de la estación de puerta.
 *
 * Va aparte del `ApiService` de los comensales a propósito: son dos productos con dos públicos y
 * dos sesiones distintas. Mezclarlos dejaría al comensal a un fallo de distancia de la lista con
 * los nombres y teléfonos de toda la fila.
 */

export type Reachable = 'TELEGRAM' | 'PHONE_ONLY';
export type PartyStatus = 'WAITING' | 'CALLED' | 'SEATED' | 'LEFT';

export interface WaitlistParty {
  readonly id: string;
  readonly name: string;
  readonly phone: string | null;
  readonly party_size: number;
  readonly zone: string | null;
  readonly notes: string | null;
  readonly status: PartyStatus;
  readonly position: number | null;
  readonly estimate_min: number | null;
  readonly estimate_max: number | null;
  readonly table_label: string | null;
  /** Si escaneó el QR se le puede avisar; si no, hay que llamarle. */
  readonly reachable: Reachable;
  readonly created_at: string;
  readonly called_at: string | null;
  readonly seated_at: string | null;
}

export interface DoorLayout {
  readonly tables: readonly { seats: number; count: number }[];
  readonly turnMinutes: number;
  readonly zones: readonly string[];
}

export interface DoorBoard {
  readonly restaurant: { id: string; name: string };
  readonly layout: DoorLayout;
  readonly parties: readonly WaitlistParty[];
}

export interface WaitEstimate {
  readonly minMinutes: number | null;
  readonly maxMinutes: number | null;
  readonly tablesThatFit: number;
  readonly ahead: number;
  readonly text: string;
}

export interface NewParty {
  readonly name: string;
  readonly phone?: string;
  readonly party_size: number;
  readonly zone?: string | null;
  readonly notes?: string | null;
}

export interface AddedParty extends WaitlistParty {
  readonly estimate_text: string;
  /** Lo que codifica el QR. Nulo cuando el servidor no tiene bot: entonces no hay aviso posible. */
  readonly join_url: string | null;
}

const BASE = import.meta.env.VITE_API_BASE_URL ?? '/api/v1';
const TOKEN_KEY = 'provecho.staff-token';

export function storedStaffToken(): string | null {
  try {
    return window.sessionStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

/**
 * El token del personal vive en `sessionStorage`, no en `localStorage`.
 *
 * Así se pierde al cerrar la pestaña: una tableta en la puerta la usa mucha gente, y una sesión
 * que sobrevive de un turno al siguiente es una sesión que alguien acaba encontrándose abierta.
 */
function storeStaffToken(token: string | null): void {
  try {
    if (token) window.sessionStorage.setItem(TOKEN_KEY, token);
    else window.sessionStorage.removeItem(TOKEN_KEY);
  } catch {
    // Almacenamiento bloqueado: la sesión dura lo que dure la pantalla.
  }
}

async function request<T>(
  method: string,
  path: string,
  { body, token }: { body?: unknown; token?: string | null } = {},
): Promise<AppResult<T>> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 15_000);
  try {
    const response = await fetch(`${BASE}${path}`, {
      method,
      signal: controller.signal,
      headers: {
        Accept: 'application/json',
        ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });

    const text = await response.text();
    const parsed: unknown = text.length > 0 ? JSON.parse(text) : null;

    if (!response.ok) {
      const fields = (parsed as { fields?: Record<string, string> } | null)?.fields ?? {};
      return failure(ErrorMapper.fromHttpCode(response.status, fields));
    }
    return success(parsed as T);
  } catch (error) {
    return failure(ErrorMapper.fromThrowable(error, currentlyConnected()));
  } finally {
    clearTimeout(timer);
  }
}

export const DoorApi = {
  async login(pin: string): Promise<AppResult<string>> {
    const result = await request<{ access_token: string }>('POST', '/staff/login', { body: { pin } });
    if (!result.ok) return result;
    if (!result.data.access_token) return failure(AppErrors.unknown('Respuesta sin token'));
    storeStaffToken(result.data.access_token);
    return success(result.data.access_token);
  },

  logout(): void {
    storeStaffToken(null);
  },

  board(restaurantId: string, token: string): Promise<AppResult<DoorBoard>> {
    return request('GET', `/waitlist/${restaurantId}`, { token });
  },

  estimate(restaurantId: string, partySize: number, token: string): Promise<AppResult<WaitEstimate>> {
    return request('GET', `/waitlist/${restaurantId}/estimate?party=${partySize}`, { token });
  },

  add(restaurantId: string, party: NewParty, token: string): Promise<AppResult<AddedParty>> {
    return request('POST', `/waitlist/${restaurantId}`, { body: party, token });
  },

  call(
    restaurantId: string,
    id: string,
    tableLabel: string | null,
    token: string,
  ): Promise<AppResult<WaitlistParty & { notified: boolean; notify_error: string | null }>> {
    return request('POST', `/waitlist/${restaurantId}/${id}/call`, {
      body: { table_label: tableLabel },
      token,
    });
  },

  seat(restaurantId: string, id: string, tableLabel: string | null, token: string): Promise<AppResult<WaitlistParty>> {
    return request('POST', `/waitlist/${restaurantId}/${id}/seat`, { body: { table_label: tableLabel }, token });
  },

  leave(restaurantId: string, id: string, token: string): Promise<AppResult<WaitlistParty>> {
    return request('POST', `/waitlist/${restaurantId}/${id}/leave`, { body: {}, token });
  },
};
